import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, LogOut, RefreshCw, Mail, Check, X } from "lucide-react";
import { fetchAdminSubmissions, verifyAdmin } from "../lib/api";
import Overline from "../components/Overline";

const TOKEN_KEY = "elle_admin_token";

const Admin = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [input, setInput] = useState("");
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authed, setAuthed] = useState(false);

  const load = async (t) => {
    setLoading(true);
    try {
      const data = await fetchAdminSubmissions(t);
      setSubmissions(data);
    } catch (e) {
      const msg = e?.response?.status === 401 ? "Invalid admin token." : "Failed to load.";
      toast.error(msg);
      if (e?.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
        setAuthed(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      verifyAdmin(token)
        .then(() => { setAuthed(true); load(token); })
        .catch(() => {
          sessionStorage.removeItem(TOKEN_KEY);
          setToken("");
          setAuthed(false);
        });
    }
    // eslint-disable-next-line
  }, []);

  const onLogin = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    try {
      await verifyAdmin(input.trim());
      sessionStorage.setItem(TOKEN_KEY, input.trim());
      setToken(input.trim());
      setAuthed(true);
      load(input.trim());
      toast.success("Welcome back.");
    } catch {
      toast.error("Invalid admin token.");
    }
  };

  const onLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setSubmissions([]);
  };

  if (!authed) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="admin-login-page">
        <form onSubmit={onLogin} className="w-full max-w-md bg-white border border-elle p-10">
          <Overline>Admin</Overline>
          <h1 className="font-serif-display text-4xl text-elle-green mt-4">Enter your token</h1>
          <p className="mt-4 text-[14px] text-elle-soft">
            Access contact submissions received through the Ell'e Coffee site.
          </p>
          <div className="mt-8 relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-elle-green/50" />
            <input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Admin token"
              data-testid="admin-token-input"
              className="w-full border border-elle pl-10 pr-4 py-3 text-[15px] outline-none focus:border-elle-green transition-colors"
            />
          </div>
          <button
            type="submit"
            data-testid="admin-login-btn"
            className="w-full mt-6 bg-elle-green text-elle-cream py-4 text-[11px] tracking-overline uppercase hover:bg-[var(--elle-green-deep)] transition-colors"
          >
            Unlock
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="py-12 md:py-20" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <Overline>Admin</Overline>
            <h1 className="font-serif-display text-5xl text-elle-green mt-3">
              Contact submissions
            </h1>
            <p className="mt-3 text-[14px] text-elle-soft">
              {submissions.length} total {submissions.length === 1 ? "message" : "messages"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => load(token)}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-2 border border-elle-green text-elle-green px-5 py-2.5 text-[11px] tracking-overline uppercase hover:bg-elle-green hover:text-elle-cream transition-colors"
            >
              <RefreshCw size={13} /> Refresh
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 text-elle-green text-[11px] tracking-overline uppercase link-underline"
            >
              <LogOut size={13} /> Log out
            </button>
          </div>
        </div>

        <div className="mt-12 space-y-4">
          {loading && <div className="text-elle-soft text-center py-10">Loading…</div>}
          {!loading && submissions.length === 0 && (
            <div className="bg-white border border-elle p-16 text-center" data-testid="admin-empty">
              <Mail className="mx-auto text-elle-green/50" size={28} />
              <p className="mt-4 font-serif-display text-2xl text-elle-green">No messages yet</p>
              <p className="text-elle-soft text-[14px] mt-2">Submissions from the contact form will appear here.</p>
            </div>
          )}
          {submissions.map((s) => (
            <div
              key={s.id}
              data-testid={`admin-submission-${s.id}`}
              className="bg-white border border-elle p-6 md:p-8"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3">
                    <span className="font-serif-display text-2xl text-elle-green">{s.name}</span>
                    <span className="text-[11px] tracking-overline uppercase text-elle-green/60">
                      {s.email}
                    </span>
                  </div>
                  {s.subject && (
                    <div className="mt-1 text-[13px] text-elle-soft italic">{s.subject}</div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-[11px] tracking-overline uppercase text-elle-green/60">
                    {new Date(s.created_at).toLocaleString()}
                  </div>
                  <div className={`text-[11px] tracking-overline uppercase mt-1 inline-flex items-center gap-1 ${s.email_sent ? "text-elle-green" : "text-elle-red"}`}>
                    {s.email_sent ? <><Check size={12}/> Email sent</> : <><X size={12}/> Email not sent</>}
                  </div>
                </div>
              </div>
              <div className="mt-4 border-t border-elle pt-4 text-[15px] text-elle-soft whitespace-pre-wrap leading-relaxed">
                {s.message}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Admin;
